/**
 * FinancialCharges - Página de Cobranças do módulo Financeiro
 * 
 * Layout premium replicando padrão CrmGeneratedContracts:
 * - Header compacto com refresh + timestamp
 * - Barra de filtros avançados (busca, status, tipo, meio pgto, produto, datas)
 * - Tabela com colunas: Cliente, Descrição, Tipo, Valor, Status, Vencimento, Data Pagamento, Parcelas, Conta Corrente, Ações
 * - Paginação server-side via RPC get_cobrancas_paginated
 * - KPIs server-side (sem limite de 1k rows)
 * 
 * @author Sistema
 * @version 3.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação da página (Etapa 1)
 * RELEASE: 2026-03-15 v1.1.0 - Botão Nova Cobrança, cards de métricas
 * RELEASE: 2026-03-15 v1.2.0 - Modal detalhes, edição, WhatsApp, reenviar provider
 * RELEASE: 2026-03-15 v2.0.0 - Layout premium padrão Contratos CRM
 * RELEASE: 2026-03-15 v3.0.0 - Paginação server-side, filtros avançados, colunas Cliente/Data Pagamento/Conta Corrente
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Progress } from '@/components/ui/progress';
import { useNavigate } from 'react-router-dom';
import { parseDateLocal } from '@/lib/utils';
import { startOfMonth, endOfMonth, format as formatDateFns } from 'date-fns';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import {
  calculatePeriodDates,
  type PeriodPreset,
} from '@/hooks/useCrmOverviewFilters';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { IntegrationHealthBanner } from '@/components/integrations/IntegrationHealthBanner';

const logger = createLogger('FinancialCharges');


import {
  Plus,
  Receipt,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  Pencil,
  MessageCircle,
  Search,
  X,
  MoreHorizontal,
  SlidersHorizontal,
  CalendarIcon,
  RefreshCw,
  Crown,
  Package,
  Paperclip,
  Repeat,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FinanceiroStatus, TIPOS_COBRANCA } from '@/hooks/useFinanceiroCobrancas';
import { useFinanceiroCobrancasPaginated, PaginatedCobranca, CobrancaPaginatedFilters } from '@/hooks/useFinanceiroCobrancasPaginated';
import { useFinanceiroMeiosPagamento, TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { useFinanceiroContaCorrente } from '@/hooks/useFinanceiroContaCorrente';
import { useFinanceiroCobrancas } from '@/hooks/useFinanceiroCobrancas';
import { ChargeProviderActions } from '@/components/financial/ChargeProviderActions';
import { CreateChargeModal } from '@/components/financial/CreateChargeModal';
import { ChargeDetailsModal } from '@/components/financial/ChargeDetailsModal';
import { PaymentMethodSelect } from '@/components/financial/PaymentMethodSelect';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CategoryFilterSelect } from '@/components/crm/CategoryFilterSelect';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
// v1.0.0 (2026-06-21): resolução com contingência checkout transparente x provider.
import { resolveCheckoutUrl } from '@/lib/financial/resolveCheckoutUrl';
import { FreeTierLimitBanner } from '@/components/financial/FreeTierLimitBanner';

/**
 * Mapa de cores para badge de status
 * Padrão text-[11px] px-2 py-0.5 rounded-full (mesmo de CrmGeneratedContracts)
 */
const statusBadgeColors: Record<FinanceiroStatus, string> = {
  aguardando_emissao: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  pendente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  edicao_andamento: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  aguardando_pagamento: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  pago: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  parcial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  vencido: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  cancelado: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  processando_cancelamento: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  falha_cancelamento: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

/** Labels de status para exibição */
const statusLabels: Record<FinanceiroStatus, string> = {
  aguardando_emissao: 'Aguardando emissão',
  pendente: 'Pendente',
  edicao_andamento: 'Edição em andamento',
  aguardando_pagamento: 'Aguardando Pagamento',
  pago: 'Pago',
  parcial: 'Parcial',
  vencido: 'Vencido',
  cancelado: 'Cancelado',
  processando_cancelamento: 'Processando cancelamento',
  falha_cancelamento: 'Falha ao cancelar',
};

// (tipoLabels removido: a coluna "Parcelas" agora unifica a exibição do tipo de cobrança)


/**
 * Cor da barra lateral por status da cobrança
 * Segue o padrão visual de CrmGeneratedContracts
 */
function getStatusBarColor(status: string): string {
  switch (status) {
    case 'aguardando_emissao':
      return 'bg-slate-400';
    case 'pendente':
      return 'bg-yellow-500';
    case 'pago':
      return 'bg-green-500';
    case 'parcial':
      return 'bg-blue-500';
    case 'vencido':
      return 'bg-red-500';
    case 'cancelado':
      return 'bg-muted-foreground/40';
    default:
      return 'bg-muted-foreground/40';
  }
}


export default function FinancialCharges() {
  // ====== Estados de filtro ======
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tipoFilter, setTipoFilter] = useState<string>('all');
  const [meioPagamentoFilter, setMeioPagamentoFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  // Debounce da busca para evitar chamadas excessivas à RPC
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Filtro de categoria de produto (botão Mais)
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  // Filtros de data avulsos (botão Mais)
  const [dataEmissaoDe, setDataEmissaoDe] = useState<string | null>(null);
  const [dataEmissaoAte, setDataEmissaoAte] = useState<string | null>(null);
  const [dataVencimentoDe, setDataVencimentoDe] = useState<string | null>(() => formatDateFns(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dataVencimentoAte, setDataVencimentoAte] = useState<string | null>(() => formatDateFns(endOfMonth(new Date()), 'yyyy-MM-dd'));
  // Estado do navegador de mês rápido (header)
  const [monthFilter, setMonthFilter] = useState<Date | null>(new Date());
  // v2.1.0 (2026-07-24) — Preset ativo do MonthNavigator (Hoje/Ontem/7d/15d/
  // Mês Atual/Mês Passado/Personalizado). Compartilha vencimento como range.
  const [activePreset, setActivePreset] = useState<PeriodPreset | null>('month');
  const [customRange, setCustomRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [dataPagamentoDe, setDataPagamentoDe] = useState<string | null>(null);
  const [dataPagamentoAte, setDataPagamentoAte] = useState<string | null>(null);
  // v3.2.0: novos filtros — data criação, conta corrente, unidade de serviço
  const [dataCriacaoDe, setDataCriacaoDe] = useState<string | null>(null);
  const [dataCriacaoAte, setDataCriacaoAte] = useState<string | null>(null);
  const [contaCorrenteFilter, setContaCorrenteFilter] = useState<string>('all');
  const [unidadeServicoFilter, setUnidadeServicoFilter] = useState<string>('all');
  // Combobox de produto (busca)
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  // Estado do Sheet lateral "Mais" (sidebar de filtros avançados)
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Estados de paginação (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Estados de modais
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCobranca, setSelectedCobranca] = useState<PaginatedCobranca | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setCreateModalOpen(true));
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailCobranca, setDetailCobranca] = useState<PaginatedCobranca | null>(null);

  // Hooks
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { hasFinancialModule, isLoadingLimits } = useResourceLimits();
  const navigate = useNavigate();
  
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { meios } = useFinanceiroMeiosPagamento();
  // v3.2.0: Hook de contas correntes (usado no novo filtro)
  const { contas: contasCorrentes = [] } = useFinanceiroContaCorrente();
  // Hook antigo apenas para deleteCobranca (mutation) — query desabilitada para evitar SELECT direto na tabela
  const { deleteCobranca } = useFinanceiroCobrancas(undefined, { enabled: false });

  // Query: contagem de cobranças ativas para controle de limite (padrão CrmContracts)
  const { data: chargeCount = 0 } = useQuery({
    queryKey: ['financeiro-cobrancas-count', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('count_active_financeiro_cobrancas' as any);
      if (error) {
        logger.error('Erro ao contar cobranças ativas', { error });
        return 0;
      }
      return data as number;
    },
    staleTime: 30000,
  });

  /** Limite de 10 cobranças sem add-on modulo_financeiro */
  const isAtLimit = !hasFinancialModule && chargeCount >= 10;

  // Query simples para listar produtos (via RPC segura)
  const { data: products = [] } = useQuery({
    queryKey: ['crm-products-simple', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase.rpc('get_tenant_products_safe', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) return [];
      return (data || []).map((row: { id: string; name: string }) => ({
        id: row.id,
        name: row.name,
      }));
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Debounce da busca textual (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Monta filtros para o hook server-side
  // Mapeia filtros avulsos diretamente para os parâmetros da RPC
  const filters: CobrancaPaginatedFilters = useMemo(() => {
    return {
      status: statusFilter !== 'all' ? statusFilter : null,
      tipo_cobranca: tipoFilter !== 'all' ? tipoFilter : null,
      meio_pagamento_id: meioPagamentoFilter !== 'all' ? meioPagamentoFilter : null,
      product_id: productFilter !== 'all' ? productFilter : null,
      category_id: categoryFilter !== 'all' ? categoryFilter : null,
      search: debouncedSearch || null,
      data_emissao_de: dataEmissaoDe,
      data_emissao_ate: dataEmissaoAte,
      data_vencimento_de: dataVencimentoDe,
      data_vencimento_ate: dataVencimentoAte,
      data_pagamento_de: dataPagamentoDe,
      data_pagamento_ate: dataPagamentoAte,
      // v3.2.0
      data_criacao_de: dataCriacaoDe,
      data_criacao_ate: dataCriacaoAte,
      conta_corrente_id: contaCorrenteFilter !== 'all' ? contaCorrenteFilter : null,
      product_term_type: unidadeServicoFilter !== 'all' ? unidadeServicoFilter : null,
    };
  }, [statusFilter, tipoFilter, meioPagamentoFilter, productFilter, categoryFilter, debouncedSearch, dataEmissaoDe, dataEmissaoAte, dataVencimentoDe, dataVencimentoAte, dataPagamentoDe, dataPagamentoAte, dataCriacaoDe, dataCriacaoAte, contaCorrenteFilter, unidadeServicoFilter]);

  // Hook de paginação server-side
  const {
    cobrancas,
    totalCount,
    kpis,
    isLoading,
    isFetching,
    refetch,
  } = useFinanceiroCobrancasPaginated(filters, { page: currentPage, pageSize });

  // Hook de refresh com timestamp (padrão Contratos)
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();

  /** Executa refresh manual dos dados */
  const handleRefresh = useCallback(() => {
    triggerRefresh(async () => {
      refetch();
    });
  }, [triggerRefresh, refetch]);

  /** Handler para mudança de mês no MonthNavigator (setas) */
  const handleMonthChange = useCallback((newDate: Date) => {
    setMonthFilter(newDate);
    setActivePreset('month');
    setCustomRange(null);
    setDataVencimentoDe(formatDateFns(startOfMonth(newDate), 'yyyy-MM-dd'));
    setDataVencimentoAte(formatDateFns(endOfMonth(newDate), 'yyyy-MM-dd'));
  }, []);

  /** Limpar filtro de mês/preset (ver todos) */
  const handleMonthClear = useCallback(() => {
    setMonthFilter(null);
    setActivePreset(null);
    setCustomRange(null);
    setDataVencimentoDe(null);
    setDataVencimentoAte(null);
  }, []);

  /**
   * v2.1.0 — Handler para selecionar preset no popover do MonthNavigator.
   * Aplica o range calculado ao filtro de vencimento (mesmo campo do modo mensal).
   */
  const handlePresetChange = useCallback((preset: PeriodPreset) => {
    setActivePreset(preset);
    if (preset === 'month') {
      const now = new Date();
      setMonthFilter(now);
      setCustomRange(null);
      setDataVencimentoDe(formatDateFns(startOfMonth(now), 'yyyy-MM-dd'));
      setDataVencimentoAte(formatDateFns(endOfMonth(now), 'yyyy-MM-dd'));
      return;
    }
    setCustomRange(null);
    const { startDate, endDate } = calculatePeriodDates(preset);
    setDataVencimentoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataVencimentoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  /** v2.1.0 — Handler para range customizado do MonthNavigator. */
  const handleCustomPeriodChange = useCallback((startDate: Date, endDate: Date) => {
    setActivePreset('custom');
    setCustomRange({ startDate, endDate });
    setDataVencimentoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataVencimentoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  /**
   * Listener Realtime centralizado para financeiro_cobrancas
   * Invalida cache da query paginada em qualquer evento
   * 
   * @version 3.0.0 — Adaptado para queryKey paginada
   */
  useEffect(() => {
    if (!currentTenant?.id) return;

    const channel = supabase
      .channel(`financeiro-cobrancas-page-rt-${currentTenant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financeiro_cobrancas',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        (payload) => {
          logger.info('Realtime: cobrança alterada', { eventType: payload.eventType });
          queryClient.invalidateQueries({
            queryKey: ['financeiro-cobrancas-paginated', currentTenant.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, queryClient]);

  // Reset página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, tipoFilter, meioPagamentoFilter, productFilter, categoryFilter, dataEmissaoDe, dataEmissaoAte, dataVencimentoDe, dataVencimentoAte, dataPagamentoDe, dataPagamentoAte, dataCriacaoDe, dataCriacaoAte, contaCorrenteFilter, unidadeServicoFilter]);

  /** Contador de filtros avançados ativos (para badge do botão Mais) */
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (productFilter !== 'all') count++;
    if (categoryFilter !== 'all') count++;
    if (dataEmissaoDe || dataEmissaoAte) count++;
    if (dataVencimentoDe || dataVencimentoAte) count++;
    if (dataPagamentoDe || dataPagamentoAte) count++;
    if (dataCriacaoDe || dataCriacaoAte) count++;
    if (contaCorrenteFilter !== 'all') count++;
    if (unidadeServicoFilter !== 'all') count++;
    return count;
  }, [productFilter, categoryFilter, dataEmissaoDe, dataEmissaoAte, dataVencimentoDe, dataVencimentoAte, dataPagamentoDe, dataPagamentoAte, dataCriacaoDe, dataCriacaoAte, contaCorrenteFilter, unidadeServicoFilter]);

  /** Verifica se há filtros ativos */
  const hasActiveFilters = search || statusFilter !== 'all' || tipoFilter !== 'all' || meioPagamentoFilter !== 'all' || advancedFilterCount > 0;

  /** Limpa todos os filtros */
  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setTipoFilter('all');
    setMeioPagamentoFilter('all');
    setProductFilter('all');
    setCategoryFilter('all');
    setDataEmissaoDe(null);
    setDataEmissaoAte(null);
    setDataVencimentoDe(null);
    setDataVencimentoAte(null);
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
    setDataCriacaoDe(null);
    setDataCriacaoAte(null);
    setContaCorrenteFilter('all');
    setUnidadeServicoFilter('all');
  }, []);

  /** Limpa apenas filtros avançados (dentro do Sheet "Mais") */
  const clearAdvancedFilters = useCallback(() => {
    setProductFilter('all');
    setCategoryFilter('all');
    setDataEmissaoDe(null);
    setDataEmissaoAte(null);
    setDataVencimentoDe(null);
    setDataVencimentoAte(null);
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
    setDataCriacaoDe(null);
    setDataCriacaoAte(null);
    setContaCorrenteFilter('all');
    setUnidadeServicoFilter('all');
  }, []);

  /** Formata valor monetário */
  const formatCurrency = (value: number, moeda = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: moeda,
    }).format(value);
  };


  /** Abre modal de detalhes ao clicar na linha */
  const handleRowClick = (cobranca: PaginatedCobranca) => {
    setDetailCobranca(cobranca);
    setDetailsOpen(true);
  };

  /** Envia link de pagamento via WhatsApp (resolveCheckoutUrl aplica contingência). */
  const handleWhatsApp = (cobranca: PaginatedCobranca) => {
    const link = resolveCheckoutUrl({
      checkout_mode: cobranca.checkout_mode,
      checkout_session_slug: cobranca.checkout_session_slug,
      provider_invoice_url: cobranca.provider_invoice_url,
      provider_payment_link: cobranca.provider_payment_link,
    }).url;
    if (!link) return;
    const valor = formatCurrency(cobranca.valor_total, cobranca.moeda);
    const msg = encodeURIComponent(
      `Olá! Segue o link para pagamento da cobrança no valor de ${valor}:\n\n${link}\n\nQualquer dúvida, estamos à disposição.`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  /** Executa soft-delete da cobrança selecionada */
  const handleDelete = () => {
    if (selectedCobranca) {
      deleteCobranca.mutate(selectedCobranca.id, {
        onSuccess: () => {
          // Invalida cache paginado após exclusão
          queryClient.invalidateQueries({
            queryKey: ['financeiro-cobrancas-paginated', currentTenant?.id],
          });
        },
      });
      setDeleteOpen(false);
      setSelectedCobranca(null);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {/* ====== Header Compacto (padrão Contatos) ====== */}
        {/* Mobile: linha 1 = título + ações | linha 2 = navegador de mês centralizado.
            Desktop (sm+): mantém layout original em 3 colunas (título | mês | ações). */}
        <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
          {/* Bloco título — no mobile abriga as ações alinhadas à direita na mesma linha */}
          <div className="flex items-center justify-between gap-2 sm:block">
            <div>
              <h1 className="text-xl font-semibold">Cobranças</h1>
              {/* Subtítulo oculto no mobile */}
              <p className="hidden sm:block text-xs text-muted-foreground">
                Gerencie suas cobranças de contas a receber
              </p>
            </div>
            {/* Ações (mobile) — mesma linha do título, alinhadas à direita */}
            <div className="flex items-center gap-1.5 sm:hidden">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              </Button>
              {canCreate('financial_charges') && (
                <Button
                  size="xs"
                  onClick={() => setCreateModalOpen(true)}
                  disabled={isAtLimit}
                  title={isAtLimit ? 'Limite de 10 cobranças atingido. Contrate o módulo financeiro.' : undefined}
                >
                  <Plus className="sm:mr-1.5" />
                  <span className="hidden sm:inline">Nova Cobrança</span>
                </Button>
              )}
            </div>
          </div>

          {/* Navegador de mês — centralizado no mobile, no centro do header no desktop */}
          <div className="flex justify-center sm:justify-self-center">
            {monthFilter || (activePreset && activePreset !== 'month') ? (
              <MonthNavigator
                currentMonth={monthFilter ?? new Date()}
                onChange={handleMonthChange}
                onClear={handleMonthClear}
                activePreset={activePreset}
                customRange={customRange}
                onPresetChange={handlePresetChange}
                onCustomChange={handleCustomPeriodChange}
              />
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMonthChange(new Date())}
                className="text-xs text-muted-foreground"
              >
                <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                Filtrar por mês
              </Button>
            )}
          </div>

          {/* Ações (desktop) — alinhadas à direita */}
          <div className="hidden sm:flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
              </Button>
            </div>
            {canCreate('financial_charges') && (
              <Button
                size="xs"
                onClick={() => setCreateModalOpen(true)}
                disabled={isAtLimit}
                title={isAtLimit ? 'Limite de 10 cobranças atingido. Contrate o módulo financeiro.' : undefined}
              >
                <Plus className="mr-1.5" />
                Nova Cobrança
              </Button>
            )}
          </div>
        </div>

        {/* Banner genérico de saúde de integrações (Asaas / futuro: outros providers).
            Renderiza apenas se houver integração problemática (failed/degraded). */}
        <IntegrationHealthBanner module="financial" className="px-4 pt-3" />

        {/* ====== KPI Cards - Métricas agregadas (padrão horizontal compacto) ====== */}
        {!isLoading && (

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-purple-100 dark:bg-purple-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Receipt className="h-5 w-5 shrink-0 text-purple-700 dark:text-purple-300" />
                    <p className="text-xs font-medium truncate">Total Cobranças</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{totalCount}</p>
                </div>
              </CardContent>
            </Card>

            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-lime-100 dark:bg-green-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-green-700 dark:text-green-300" />
                    <p className="text-xs font-medium truncate">Faturas Pagas</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">
                    {kpis.totalPagas}/{kpis.totalFaturas}
                    <span className="text-[10px] font-normal text-muted-foreground ml-1">pagas</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-blue-100 dark:bg-blue-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" />
                    <p className="text-xs font-medium truncate">Recebido</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{formatCurrency(kpis.recebido)}</p>
                </div>
              </CardContent>
            </Card>

            <Card className={cn('border-0 shadow-none transition-all hover:shadow-md bg-cyan-100 dark:bg-cyan-900/30')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="h-5 w-5 shrink-0 text-cyan-700 dark:text-cyan-300" />
                    <p className="text-xs font-medium truncate">Previsto</p>
                  </div>
                  <p className="text-xl font-bold leading-tight shrink-0">{formatCurrency(kpis.previsto)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ====== Banner — Módulo Financeiro gratuito limitado (componente compartilhado) ====== */}
        <FreeTierLimitBanner entityLabel="cobranças" />


        <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
          {/* Busca livre expandida */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
            <Input
              placeholder="Cliente, CPF/CNPJ, email, telefone, valor, ID, N.Fatura..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
            />
          </div>

          {/* Filtro de Status */}
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v)}>
            <SelectTrigger className="w-[85px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro de Tipo — v3.2.0: adiciona opção "Recorrente" (sintético: cobranças com assinatura) */}
          <Select value={tipoFilter} onValueChange={v => setTipoFilter(v)}>
            <SelectTrigger className="w-[85px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos tipos</SelectItem>
              {TIPOS_COBRANCA.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
              <SelectItem value="recorrente">Recorrente</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro de Meio de Pagamento — v3.2.0: reuso de PaymentMethodSelect (ícones + ativos/inativos) */}
          <PaymentMethodSelect
            value={meioPagamentoFilter}
            onValueChange={(v) => setMeioPagamentoFilter(v)}
            placeholder="Meio Pgto"
            allOptionLabel="Todos meios"
            className="w-[100px] h-6 text-[11px] sm:w-[180px] sm:h-8 sm:text-sm"
          />

          {/* ====== Botão "Mais" — abre Sheet lateral de Filtros Avançados ====== */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMoreFiltersOpen(true)}
            className="h-6 px-2 text-[10px] sm:h-8 sm:px-2.5 sm:text-xs rounded-full gap-1.5"
          >
            <SlidersHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            <span>Mais</span>
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
                {advancedFilterCount}
              </Badge>
            )}
          </Button>

          {/* ====== Sheet lateral — Filtros Avançados com scroll interno ====== */}
          <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
            <SheetContent side="right" className="w-80 p-0 flex flex-col">
              <SheetHeader className="px-4 pt-4 pb-3 border-b">
                <SheetTitle className="flex items-center justify-between text-sm font-semibold">
                  <span>Filtros Avançados</span>
                  {advancedFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAdvancedFilters}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Limpar
                    </Button>
                  )}
                </SheetTitle>
              </SheetHeader>
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-4 space-y-4">
                  {/* Filtro de Produto/Serviço — v3.2.0: Combobox com busca */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Produto/Serviço</Label>
                    <Popover open={productPickerOpen} onOpenChange={setProductPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          size="sm"
                          className={cn(
                            'w-full h-8 text-sm justify-between font-normal',
                            productFilter === 'all' && 'text-muted-foreground'
                          )}
                        >
                          <span className="truncate">
                            {productFilter === 'all'
                              ? 'Todos produtos'
                              : products.find(p => p.id === productFilter)?.name || 'Selecionado'}
                          </span>
                          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Buscar produto..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__all__"
                                onSelect={() => { setProductFilter('all'); setProductPickerOpen(false); }}
                              >
                                <Check className={cn('mr-2 h-4 w-4', productFilter === 'all' ? 'opacity-100' : 'opacity-0')} />
                                Todos produtos
                              </CommandItem>
                              {products.map(p => (
                                <CommandItem
                                  key={p.id}
                                  value={p.name}
                                  onSelect={() => { setProductFilter(p.id); setProductPickerOpen(false); }}
                                >
                                  <Check className={cn('mr-2 h-4 w-4', productFilter === p.id ? 'opacity-100' : 'opacity-0')} />
                                  {p.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Filtro de Categoria — CategoryFilterSelect hierárquico (mesmo do Kanban CRM) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Categoria</Label>
                    <CategoryFilterSelect
                      value={categoryFilter !== 'all' ? categoryFilter : null}
                      onChange={(id) => setCategoryFilter(id || 'all')}
                      placeholder="Todas categorias"
                      size="xs"
                    />
                  </div>

                  {/* v3.2.0: Unidade de serviço (produto único vs recorrente) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Unidade de Serviço</Label>
                    <Select value={unidadeServicoFilter} onValueChange={setUnidadeServicoFilter}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="one_time">Único</SelectItem>
                        <SelectItem value="recurring">Recorrente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* v3.2.0: Conta Corrente */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Conta Corrente</Label>
                    <Select value={contaCorrenteFilter} onValueChange={setContaCorrenteFilter}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Todas contas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas contas</SelectItem>
                        {contasCorrentes
                          .filter((cc) => cc.is_active)
                          .map((cc) => (
                            <SelectItem key={cc.id} value={cc.id}>
                              {cc.nome}
                              {cc.banco_nome ? <span className="text-muted-foreground"> · {cc.banco_nome}</span> : null}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  {/* Filtro Data Emissão (avulso) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data Emissão</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataEmissaoDe && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataEmissaoDe ? format(parseDateLocal(dataEmissaoDe), 'dd/MM/yy') : 'De'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataEmissaoDe ? parseDateLocal(dataEmissaoDe) : undefined}
                            onSelect={(d) => setDataEmissaoDe(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataEmissaoAte && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataEmissaoAte ? format(parseDateLocal(dataEmissaoAte), 'dd/MM/yy') : 'Até'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataEmissaoAte ? parseDateLocal(dataEmissaoAte) : undefined}
                            onSelect={(d) => setDataEmissaoAte(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Filtro Data Vencimento (avulso) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data Vencimento</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataVencimentoDe && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataVencimentoDe ? format(parseDateLocal(dataVencimentoDe), 'dd/MM/yy') : 'De'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataVencimentoDe ? parseDateLocal(dataVencimentoDe) : undefined}
                            onSelect={(d) => setDataVencimentoDe(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataVencimentoAte && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataVencimentoAte ? format(parseDateLocal(dataVencimentoAte), 'dd/MM/yy') : 'Até'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataVencimentoAte ? parseDateLocal(dataVencimentoAte) : undefined}
                            onSelect={(d) => setDataVencimentoAte(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Filtro Data Pagamento (avulso) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data Pagamento</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataPagamentoDe && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataPagamentoDe ? format(parseDateLocal(dataPagamentoDe), 'dd/MM/yy') : 'De'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataPagamentoDe ? parseDateLocal(dataPagamentoDe) : undefined}
                            onSelect={(d) => setDataPagamentoDe(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataPagamentoAte && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataPagamentoAte ? format(parseDateLocal(dataPagamentoAte), 'dd/MM/yy') : 'Até'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataPagamentoAte ? parseDateLocal(dataPagamentoAte) : undefined}
                            onSelect={(d) => setDataPagamentoAte(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* v3.2.0: Filtro Data Criação */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data Criação</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataCriacaoDe && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataCriacaoDe ? format(parseDateLocal(dataCriacaoDe), 'dd/MM/yy') : 'De'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataCriacaoDe ? parseDateLocal(dataCriacaoDe) : undefined}
                            onSelect={(d) => setDataCriacaoDe(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className={cn('flex-1 h-8 text-xs justify-start', !dataCriacaoAte && 'text-muted-foreground')}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {dataCriacaoAte ? format(parseDateLocal(dataCriacaoAte), 'dd/MM/yy') : 'Até'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dataCriacaoAte ? parseDateLocal(dataCriacaoAte) : undefined}
                            onSelect={(d) => setDataCriacaoAte(d ? format(d, 'yyyy-MM-dd') : null)}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>

          {/* Botão Limpar Filtros */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}

          {/* Indicador de carregamento */}
          {isFetching && !isLoading && (
            <span className="text-xs text-muted-foreground animate-pulse">
              Atualizando...
            </span>
          )}
        </div>


        {/* ====== Tabela Premium com setinhas de scroll horizontal ====== */}
        <ScrollIndicators className="flex-1">
          <div className="h-full w-full overflow-hidden">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : cobrancas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Receipt className="h-12 w-12 text-muted-foreground/40" />
              <div className="text-center">
                <h3 className="font-medium text-sm">Nenhuma cobrança encontrada</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {hasActiveFilters
                    ? 'Tente ajustar os filtros de busca'
                    : 'Clique em "Nova Cobrança" para começar'}
                </p>
              </div>
            </div>
          ) : (
            <Table scrollViewport containerClassName="h-full" className="min-w-[1900px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {/* Barra colorida de status */}
                  <TableHead className="w-2 p-0" />
                  {/* v2.0.0: ID curto (8 chars do UUID) */}
                  <TableHead className="text-xs font-medium">ID</TableHead>
                  {/* v3.2.1: Dt. Criação movida para logo após o ID */}
                  <TableHead className="text-xs font-medium">Dt. Criação</TableHead>
                  <TableHead className="text-xs font-medium">Cliente</TableHead>
                  <TableHead className="text-xs font-medium">Descrição</TableHead>
                  {/* v3.2.1: Parcelas (Recorrente/Parcelada/Única) movida para logo após Descrição */}
                  <TableHead className="text-xs font-medium">Parcelas</TableHead>
                  {/* v2.0.0: Número da fatura (primeira parcela) */}
                  <TableHead className="text-xs font-medium">N. Fatura</TableHead>
                  {/* v3.1.0: Coluna "Tipo" removida — informação consolidada em "Parcelas" */}
                  <TableHead className="text-xs font-medium">Valor</TableHead>
                  {/* v1.6.0: Colunas de progresso de pagamento */}
                  <TableHead className="text-xs font-medium">Vlr. Restante</TableHead>
                  <TableHead className="text-xs font-medium w-[120px]">% Pago</TableHead>
                  <TableHead className="text-xs font-medium">Status</TableHead>
                  {/* v3.1.0: Nova coluna — origem da baixa (manual / sistema / misto) */}
                  <TableHead className="text-xs font-medium">Origem</TableHead>
                  <TableHead className="text-xs font-medium">Vencimento</TableHead>
                  <TableHead className="text-xs font-medium">Dt. Pagamento</TableHead>
                  {/* v3.1.0: Nova coluna — comprovante de baixa manual anexado */}
                  <TableHead className="text-xs font-medium w-12 text-center">Anexo</TableHead>
                  <TableHead className="text-xs font-medium">Meio Pagamento</TableHead>
                   <TableHead className="text-xs font-medium">Conta Corrente</TableHead>
                   {/* Coluna de ações do provider (QR Code, Copiar, Link) */}
                   <TableHead className="w-24" />
                   {/* Coluna de ações */}
                   <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {cobrancas.map(cobranca => (
                  <TableRow
                    key={cobranca.id}
                    className="cursor-pointer hover:bg-muted/40 group"
                    onClick={() => handleRowClick(cobranca)}
                  >
                    {/* Barra lateral colorida baseada no status */}
                    <TableCell className="p-0 w-2">
                      <div className={cn(
                        'w-1 h-full min-h-[40px] rounded-full',
                        getStatusBarColor(cobranca.status)
                      )} />
                    </TableCell>
                    {/* v3.2.1: ID curto — padrão igual à listagem de parcelas (span simples, sem Badge) */}
                    <TableCell className="py-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {cobranca.id.substring(0, 8).toUpperCase()}
                      </span>
                    </TableCell>

                    {/* v3.2.1: Dt. Criação movida para logo após o ID */}
                    <TableCell className="py-2">
                      {cobranca.created_at ? (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(cobranca.created_at), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* Cliente + CPF/CNPJ (v1.1.0) */}
                    <TableCell className="py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate max-w-[150px]">
                          {cobranca.customer_name || 'Sem cliente'}
                        </p>
                        {cobranca.customer_document && (
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {formatDocumentForDisplay(cobranca.customer_document)}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    {/* Descrição */}
                    <TableCell className="py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate max-w-[180px]">
                          {cobranca.descricao || 'Sem descrição'}
                        </p>
                        {cobranca.observacoes && (
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {cobranca.observacoes}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    {/* v3.2.1: Parcelas — tipo lógico (Recorrente / Parcelada / Única) movida para logo após Descrição */}
                    <TableCell className="py-2">
                      {(() => {
                        if (cobranca.assinatura_id) {
                          return (
                            <div className="flex flex-col leading-tight">
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 h-4 w-fit bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20"
                              >
                                <Repeat className="h-2.5 w-2.5 mr-1" /> Recorrente
                              </Badge>
                              {cobranca.ciclo_numero && (
                                <span className="text-[10px] text-muted-foreground mt-0.5">
                                  Ciclo #{cobranca.ciclo_numero}
                                </span>
                              )}
                            </div>
                          );
                        }
                        if (cobranca.tipo_cobranca === 'parcelada') {
                          return (
                            <div className="flex flex-col leading-tight">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 w-fit">
                                Parcelada
                              </Badge>
                              <span className="text-[10px] text-muted-foreground mt-0.5">
                                {cobranca.numero_parcelas}x
                              </span>
                            </div>
                          );
                        }
                        return (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 w-fit">
                            Única
                          </Badge>
                        );
                      })()}
                    </TableCell>

                    {/* v2.0.0: N. Fatura — número da fatura no provider (primeira parcela) */}
                    <TableCell className="py-2">
                      <span className="text-xs font-mono text-muted-foreground">
                        {(cobranca as any).numero_fatura || '—'}
                      </span>
                    </TableCell>

                    {/* v3.1.0: Coluna "Tipo" removida — consolidada em "Parcelas" */}

                    {/* Valor */}
                    <TableCell className="py-2">
                      <span className="text-sm font-mono font-medium">
                        {formatCurrency(cobranca.valor_total, cobranca.moeda)}
                      </span>
                    </TableCell>

                    {/* v1.6.0: Vlr. Restante */}
                    <TableCell className="py-2">
                      {(() => {
                        const recebido = Number(cobranca.valor_recebido || 0);
                        const restante = Math.max(cobranca.valor_total - recebido, 0);
                        const isPago = cobranca.status === 'pago';
                        return (
                          <span className={cn(
                            'text-sm font-mono font-medium',
                            isPago ? 'text-emerald-600' : ''
                          )}>
                            {formatCurrency(isPago ? 0 : restante, cobranca.moeda)}
                          </span>
                        );
                      })()}
                    </TableCell>

                    {/* v1.6.0: % Pago (barra de progresso) */}
                    <TableCell className="py-2">
                      {(() => {
                        const recebido = Number(cobranca.valor_recebido || 0);
                        const percent = cobranca.valor_total > 0
                          ? Math.round((recebido / cobranca.valor_total) * 100)
                          : 0;
                        return (
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <Progress
                              value={percent}
                              className="h-2 flex-1"
                              indicatorClassName={cn(
                                percent >= 100 ? 'bg-emerald-500' :
                                percent > 0 ? 'bg-blue-500' :
                                'bg-muted-foreground/30'
                              )}
                            />
                            <span className="text-[11px] text-muted-foreground w-8 text-right">
                              {percent}%
                            </span>
                          </div>
                        );
                      })()}
                    </TableCell>

                    {/* Status */}
                    <TableCell className="py-2">
                      <span className={cn(
                        'text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                        statusBadgeColors[cobranca.status as FinanceiroStatus] || 'bg-gray-100 text-gray-800'
                      )}>
                        {statusLabels[cobranca.status as FinanceiroStatus] || cobranca.status}
                      </span>
                    </TableCell>

                    {/* v3.1.0: Origem da baixa (manual / sistema / misto) */}
                    <TableCell className="py-2">
                      {(() => {
                        const src = cobranca.paid_source;
                        if (!src) return <span className="text-xs text-muted-foreground/50">—</span>;
                        const cfg =
                          src === 'manual'
                            ? { label: 'Manual', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
                            : src === 'provider_webhook'
                              ? { label: 'Sistema', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' }
                              : { label: 'Misto', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
                        return (
                          <span className={cn('text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap', cfg.cls)}>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </TableCell>

                    {/* Vencimento */}
                    <TableCell className="py-2">
                      {cobranca.data_vencimento ? (
                        <span className="text-xs text-muted-foreground">
                          {format(parseDateLocal(cobranca.data_vencimento), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* v3.2.1: Dt. Criação removida daqui — agora renderizada logo após o ID */}



                    {/* Data Pagamento */}
                    <TableCell className="py-2">
                      {cobranca.primeira_data_pagamento ? (
                        <span className="text-xs text-muted-foreground">
                          {format(parseDateLocal(cobranca.primeira_data_pagamento), 'dd/MM/yy', { locale: ptBR })}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                    {/* v3.1.0: Anexo (comprovante de baixa manual) */}
                    <TableCell className="py-2 text-center">
                      {cobranca.has_paid_receipt ? (
                        <Paperclip className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 inline-block" />
                      ) : (
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground/20 inline-block" />
                      )}
                    </TableCell>

                    {/* v3.2.1: Parcelas removida daqui — agora renderizada logo após Descrição */}



                    {/* Meio de Pagamento */}
                    <TableCell className="py-2">
                      {cobranca.tipo_pagamento ? (
                        <span className="text-xs text-muted-foreground">
                          {TIPOS_PAGAMENTO.find(t => t.value === cobranca.tipo_pagamento)?.label || cobranca.tipo_pagamento}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                    </TableCell>

                     {/* Conta Corrente (substituindo Provider) */}
                    <TableCell className="py-2">
                      {cobranca.conta_corrente_nome ? (
                        <span className="text-xs text-muted-foreground truncate max-w-[120px] block">
                          {cobranca.conta_corrente_nome}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">-</span>
                      )}
                     </TableCell>

                     {/* Ações do Provider (QR Code PIX, Copiar link, Abrir link) */}
                     <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                       {cobranca.provider_charge_id && cobranca.conta_corrente_id &&
                        (cobranca.status === 'aguardando_pagamento' || cobranca.status === 'pago' || cobranca.status === 'parcial') && (
                         <ChargeProviderActions
                           providerChargeId={cobranca.provider_charge_id}
                           contaCorrenteId={cobranca.conta_corrente_id}
                           providerInvoiceUrl={cobranca.provider_invoice_url}
                           providerPaymentLink={cobranca.provider_payment_link}
                           providerSlug={cobranca.provider_slug}
                           status={cobranca.status}
                            billingType={cobranca.tipo_pagamento}
                            tipoCobranca={cobranca.tipo_cobranca}
                            checkoutMode={cobranca.checkout_mode}
                            checkoutSessionSlug={cobranca.checkout_session_slug}
                          />
                       )}
                     </TableCell>

                     {/* Ações (DropdownMenu padrão Contratos) */}
                    <TableCell className="py-2" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {/* Detalhes */}
                          <DropdownMenuItem onClick={() => handleRowClick(cobranca)}>
                            <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
                            Ver Detalhes
                          </DropdownMenuItem>

                          {/* WhatsApp */}
                          {(cobranca.provider_invoice_url || cobranca.provider_payment_link) && (
                            <DropdownMenuItem onClick={() => handleWhatsApp(cobranca)}>
                              <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
                              Enviar via WhatsApp
                            </DropdownMenuItem>
                          )}

                          {/* Editar (só pendente + permissão) */}
                          {cobranca.status === 'pendente' && canEdit('financial_charges') && (
                            <DropdownMenuItem onClick={() => {
                              setDetailCobranca(cobranca);
                              setDetailsOpen(true);
                            }}>
                              <Pencil className="h-4 w-4 mr-2 text-muted-foreground" />
                              Editar
                            </DropdownMenuItem>
                          )}

                          {/* Excluir (com permissão, desabilitado se pago/cancelado) */}
                          {canDelete('financial_charges') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={cobranca.status === 'pago' || cobranca.status === 'cancelado'}
                                onClick={() => {
                                  setSelectedCobranca(cobranca);
                                  setDeleteOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          </div>
        </ScrollIndicators>

        {/* ====== Footer de Paginação (server-side) ====== */}
        {!isLoading && totalCount > 0 && (
          <div className="border-t px-4 py-2 bg-background">
            <PaginationFooter
              totalItems={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              pageSizeOptions={[10, 20, 50, 100]}
              borderClassName=""
              className="mt-0 pt-0"
            />
          </div>
        )}

        {/* Modal de criação de cobrança */}
        <CreateChargeModal open={createModalOpen} onOpenChange={setCreateModalOpen} />

        {/* Modal de detalhes da cobrança — adapta PaginatedCobranca para FinanceiroCobranca */}
        <ChargeDetailsModal
          cobranca={detailCobranca as any}
          open={detailsOpen}
          onOpenChange={(v) => {
            setDetailsOpen(v);
            if (!v) setDetailCobranca(null);
          }}
        />

        {/* Confirmação de exclusão */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja cancelar esta cobrança? As parcelas associadas também serão canceladas.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
